import axios from "axios";
import React, { useEffect, useState } from "react";
import { AiOutlineDelete as DeleteIcon } from "react-icons/ai";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../routes/routeKeys";
import DownArrow from "../../../assets/svgs/down-arrow.svg";
import UpArrow from "../../../assets/svgs/up-arrow.svg";
import DeleteConfirmationModal from "../../../components/ConfirmationModals/DeleteConfirmationModal";
import AddPortfolioModal from "../../../components/DetailTabsData/Modals/AddPortfolioModal";
import Loader from "../../../components/Loader";
import Modal from "../../../components/Modal/Modal";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";

const PropertyPortfolioModal = ({ from, isOpen, onClose, id }) => {
  const [config] = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [portfolioData, setPortfolioData] = useState([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState("");
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [expandedRows, setExpandedRows] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState("portfolio"); // "portfolio" or "property"
  const [showAddModal, setShowAddModal] = useState(false);
  const [disableDelete, setDisableDelete] = useState(false);

  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);

  const handleClose = () => {
    setExpandedRows({});
    setSelectedPortfolioId("");
    setSelectedPropertyId("");
    onClose();
  };

  const openDeleteModal = (type, portfolioId, propertyId = null) => {
    setDeleteType(type);
    setSelectedPortfolioId(portfolioId);
    setSelectedPropertyId(propertyId || "");
    setShowDeleteModal(true);
  };

  const handleDelete = () => {
    setDisableDelete(true);
    let url = "";
    let successMessage = "";

    if (deleteType === "portfolio") {
      url = `${BASE_URL}/delete-portfolio/${selectedPortfolioId}`;
      successMessage = "Portfolio deleted successfully";
    } else if (deleteType === "property") {
      // IMPORTANT: Most likely NEEDS correction — confirm with backend team
      // Common patterns: /portfolio-property/${selectedPropertyId} or /portfolio/${portfolioId}/property/${propertyId}
      url = `${BASE_URL}/delete-portfolio-property/${selectedPropertyId}`; // ← adjust this!
      successMessage = "Property removed from portfolio";
    }

    axios
      .delete(url, config)
      .then((res) => {
        setShowDeleteModal(false);
        setSelectedPortfolioId("");
        setSelectedPropertyId("");
        fetchPortfolio();
        NotificationManager.success(res?.data?.message || successMessage);
      })
      .catch((err) => {
        const msg = err.response?.data?.message;
        if (msg && msg !== "Too Many Attempts.") {
          NotificationManager.error(msg);
        }
      })
      .finally(() => setDisableDelete(false));
  };

  const fetchPortfolio = () => {
    setLoading(true);
    axios
      .get(`${BASE_URL}/portfolio-getbyid/${id}`, config)
      .then((res) => {
        const portfolio = res?.data?.data;
        setPortfolioData(portfolio ? [portfolio] : []);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
        setPortfolioData([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (id && isOpen) {
      fetchPortfolio();
    } else {
      setPortfolioData([]);
    }
  }, [id, isOpen]);

  const toggleRowDetails = (portfolioId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [portfolioId]: !prev[portfolioId],
    }));
  };

  return (
    <Modal
      title="Portfolio Detail"
      width="900px"
      show={isOpen}
      onClose={handleClose}
    >
      <div className="light-bg-L pb-3 table-container w-full overflow-hidden">
        <div className="w-full table-info light-bg-L overflow-x-auto">
          {loading ? (
            <Loader />
          ) : portfolioData.length === 0 ? (
            <p className="body-N text-center mt-6 p-5">No Portfolio Available</p>
          ) : (
            <table className="contact-table light-bg-L">
              <thead>
                <tr className="uppercase body-N dark-M">
                  <th>
                    <div className="table-header">Property Info</div>
                  </th>
                  <th>
                    <div className="table-header">Portfolio Name</div>
                  </th>
                  <th>
                    <div className="table-header">Description</div>
                  </th>
                  <th>
                    <div className="table-header">Delete</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {portfolioData.map((el) => {
                  const properties = el?.portfolois ?? el?.portfolios ?? [];
                  return (
                    <React.Fragment key={el.id}>
                      {/* Main portfolio row */}
                      <tr className="body-N dark-H">
                        <td>
                          <img
                            role="button"
                            src={expandedRows[el.id] ? UpArrow : DownArrow}
                            alt="toggle"
                            className="sidebar-icons"
                            onClick={() => toggleRowDetails(el.id)}
                          />
                        </td>
                        <td className="capitalize head-5">{el?.name}</td>
                        <td className="capitalize head-5">{el?.description || "—"}</td>
                        <td>
                          <button
                            onClick={() => openDeleteModal("portfolio", el.id)}
                            disabled={disableDelete}
                            className="p-2 w-8 h-8 inline-flex items-center justify-center hover:bg-red-100 rounded-full transition-colors"
                            title="Delete Portfolio"
                          >
                            <DeleteIcon size={18} className="sidebar-icons" />
                          </button>
                        </td>
                      </tr>

                      {/* Expanded properties list */}
                      {expandedRows[el.id] && (
                        <tr>
                          <td colSpan="4">
                            <div className="expanded-row-content p-6 bg-[rgb(244,244,244)] rounded-md">
                              <div className="grid grid-cols-[1.5fr_1.5fr_1fr_1fr_0.5fr] gap-4 mb-5 font-semibold dark-H head-4 whitespace-normal">
                                <div>Property Name</div>
                                <div>Owner Name</div>
                                <div>Asking Price</div>
                                <div>Asking Cap Rate</div>
                                <div className="text-center">Delete</div>
                              </div>

                              {properties.length === 0 ? (
                                <p className="body-N text-center py-4">No properties in this portfolio</p>
                              ) : (
                                properties.map((pf, idx) => (
                                  <div
                                    key={pf.id || idx}
                                    className="grid grid-cols-[1.5fr_1.5fr_1fr_1fr_0.5fr] gap-4 mb-4 border-b border-gray-200 pb-4 last:border-b-0 last:pb-0 items-center whitespace-normal"
                                  >
                                    <p className="body-N dark-H">
                                      <span
                                        role="button"
                                        className="hover:underline cursor-pointer"
                                        onClick={() => navigate(ROUTES.PROPERTY_DETAILS.replace(":id", pf.property_id))}
                                      >
                                        {pf?.property?.property_name}
                                        <br />
                                        {[pf?.property?.city, pf?.property?.state]
                                          .filter(Boolean)
                                          .join(", ")}
                                      </span>
                                    </p>

                                    <p className="body-N dark-H">
                                      <span className="green-H body-S">
                                        {pf?.property?.owner?.name || "—"}
                                      </span>
                                    </p>

                                    <p className="body-N dark-H">
                                      <span className="green-H body-S">
                                        ${pf?.property?.asking_price_format || "0"}
                                      </span>
                                    </p>

                                    <p className="body-N dark-H">
                                      <span className="green-H body-S">
                                        {pf?.property?.asking_cap_rate_format || "0"}%
                                      </span>
                                    </p>

                                    <div className="flex justify-center">
                                      <button
                                        onClick={() => openDeleteModal("property", el.id, pf.id)}
                                        disabled={disableDelete}
                                        className="p-2 w-8 h-8 inline-flex items-center justify-center hover:bg-red-100 rounded-full transition-colors"
                                        title="Remove Property"
                                      >
                                        <DeleteIcon size={18} className="sidebar-icons" />
                                      </button>
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modals */}
      <DeleteConfirmationModal
        showModal={showDeleteModal}
        disable={disableDelete}
        onClose={() => setShowDeleteModal(false)}
        handleDelete={handleDelete}
        title={
          deleteType === "portfolio"
            ? "Delete Portfolio"
            : "Remove Property from Portfolio"
        }
        message={
          deleteType === "portfolio"
            ? "Are you sure you want to delete this entire portfolio?"
            : "Are you sure you want to remove this property from the portfolio?"
        }
      />

      <AddPortfolioModal
        showModal={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCallApi={() => {
          setShowAddModal(false);
          fetchPortfolio();
        }}
        from={from}
        id={id}
      />
    </Modal>
  );
};

export default PropertyPortfolioModal;